import { type BoundaryRequest, BoundaryRequestKind } from '@features/fields/api/useGenerateBoundary'
import { useCffForPlanningField } from 'shared-ui/field-data'
import { type AsAppliedLayer, useAsAppliedLayerOrNull } from 'shared-ui/map'
import { sameCff, type Stream, useAllStreams } from 'shared-ui/operation-wide-data'
import { type BoundarySource, rankBoundarySources } from 'utils/boundary-autogen'

export enum AutoBoundaryStatus {
  Loading = 'loading',
  Unavailable = 'unavailable',
  Ready = 'ready'
}

export type AutoBoundary =
  | { status: AutoBoundaryStatus.Loading }
  | { status: AutoBoundaryStatus.Unavailable }
  | { status: AutoBoundaryStatus.Ready; request: BoundaryRequest }

/**
 * A selected as-applied layer wins, so that the boundary follows the map the user is looking at.
 * Otherwise the best-ranked group of the field's passes is used.
 */
const decideAutoBoundary = (opts: {
  activeLayer: AsAppliedLayer | null
  sources: BoundarySource<Stream>[] | undefined
}): AutoBoundary => {
  if (opts.activeLayer !== null) {
    return {
      status: AutoBoundaryStatus.Ready,
      request: { kind: BoundaryRequestKind.ActiveLayer, layer: opts.activeLayer }
    }
  }
  if (opts.sources === undefined) return { status: AutoBoundaryStatus.Loading }

  const [bestSource] = opts.sources
  if (bestSource === undefined) return { status: AutoBoundaryStatus.Unavailable }
  return {
    status: AutoBoundaryStatus.Ready,
    request: { kind: BoundaryRequestKind.Passes, source: bestSource }
  }
}

/**
 * Decides what auto create outlines for a planning field, and ranks the field's passes so the user
 * can outline the boundary from a different group instead.
 */
export const useBoundaryAutogen = (planningFieldId: string) => {
  const activeLayer = useAsAppliedLayerOrNull()
  const cff = useCffForPlanningField(planningFieldId)
  const allStreams = useAllStreams()

  // A pass still in progress has covered only part of the field.
  const sources =
    cff === undefined || allStreams.data === undefined
      ? undefined
      : rankBoundarySources(
          allStreams.data.filter((stream) => !stream.isLive && sameCff(stream, cff))
        )

  return {
    autoBoundary: decideAutoBoundary({ activeLayer, sources }),
    sources
  }
}
